import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Checkbox, CircularProgress, IconButton, MenuItem, TextField } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons';
import { useForceRender } from 'hooks/useForceRender';
import { IRefIds } from 'constants/refs';
import TranslationUnifiedDashboardModel, {
  IUnifiedDashboardCell,
  IUnifiedDialect,
  IUnifiedLanguage,
} from 'models/TranslationUnifiedDashboardModel';
import TranslationBatchModel, { BatchJobMode } from 'models/TranslationBatchModel';
import { translationProgressRenderer } from 'CMFW/grid/cellRenderers/translationProgressRenderer';

interface IProps {
  batchModel: TranslationBatchModel;
  onCreated: (dialectId: number) => void;
  onCancel: () => void;
}

// Key for a selected cell: "ref_courseId"
type CellKey = string;
const makeCellKey = (ref: number, courseId: number): CellKey => `${ref}_${courseId}`;
const parseCellKey = (key: CellKey): { ref: number; courseId: number } => {
  const [ref, courseId] = key.split('_').map(Number);
  return { ref, courseId };
};

const sharedRefs = [
  IRefIds.bankKLine,
  IRefIds.bankPanelIntro,
  IRefIds.bankParagraph,
  IRefIds.bankTableLiteral,
  IRefIds.bankPanelTitle,
  IRefIds.bankLessonTitle,
  IRefIds.bankTrainerTitle,
  IRefIds.vocabularyCategories,
  IRefIds.hanziComponentName,
];

const UnifiedBatchCreate: React.FC<IProps> = ({ batchModel, onCreated, onCancel }) => {
  const forceRender = useForceRender();
  const model = useMemo(() => new TranslationUnifiedDashboardModel({ render: forceRender }), [forceRender]);

  const [mode, setMode] = useState<number>(BatchJobMode.FULL);
  const [dialectId, setDialectId] = useState(0);
  const [languageId, setLanguageId] = useState(0);
  const [selectedCells, setSelectedCells] = useState<Set<CellKey>>(new Set());
  const [courseRanges, setCourseRanges] = useState<{ [courseId: string]: { start: number; end: number } }>({});

  useEffect(() => {
    model.loadDialects();
  }, [model]);

  const dialects = model.getDialects();
  const languages = model.getLanguages();
  const dashboardData = model.getDashboardData();

  const handleDialectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    setDialectId(val);
    model.setDialectId(val);
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    setLanguageId(val);
    model.setLanguageId(val);
  };

  const handleLoad = () => {
    setCourseRanges({});
    setSelectedCells(new Set());
    model.getDashboard();
  };

  // Sync local ranges when dashboard data arrives
  useEffect(() => {
    if (dashboardData) {
      setCourseRanges((prev) => {
        const ranges: { [courseId: string]: { start: number; end: number } } = {};
        dashboardData.courses.forEach((c) => {
          ranges[String(c.id)] = prev[String(c.id)] || { start: c.startLesson, end: c.endLesson };
        });
        return ranges;
      });
    }
  }, [dashboardData]);

  const handleCourseRangeChange = (cId: number, field: 'start' | 'end', value: number) => {
    setCourseRanges((prev) => ({
      ...prev,
      [String(cId)]: { ...prev[String(cId)], [field]: value },
    }));
  };

  const handleReloadCourse = (cId: number) => {
    const range = courseRanges[String(cId)];
    if (range) {
      model.reloadCourse(cId, range.start, range.end);
    }
  };

  const handleCellToggle = (ref: number, courseId: number) => {
    const key = makeCellKey(ref, courseId);
    setSelectedCells((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const handleCreate = async () => {
    if (selectedCells.size === 0 || dialectId === 0) return;

    // Build selections: per-course refs and ranges
    const selections: { [courseId: number]: { refs: number[]; start: number; end: number } } = {};
    selectedCells.forEach((key) => {
      const { ref, courseId } = parseCellKey(key);
      if (!selections[courseId]) {
        const range = courseRanges[String(courseId)] || { start: 1, end: 1000 };
        selections[courseId] = { refs: [], start: range.start, end: range.end };
      }
      if (!selections[courseId].refs.includes(ref)) {
        selections[courseId].refs.push(ref);
      }
    });

    await batchModel.createBatch(dialectId, selections, mode);

    onCreated(dialectId);
  };

  // Group dialects by language
  const dialectsByLanguage: { [lang: string]: IUnifiedDialect[] } = {};
  dialects.forEach((d) => {
    if (!dialectsByLanguage[d.languageName]) {
      dialectsByLanguage[d.languageName] = [];
    }
    dialectsByLanguage[d.languageName].push(d);
  });

  const canLoad = dialectId > 0 && languageId !== 0;

  // Refs that produce one job per ref+language (must match backend isLanguageDependentRef)
  const languageDependentRefs = [
    IRefIds.sentences, // 6
    IRefIds.dialogSegment, // 66
    IRefIds.readingText, // 58
    IRefIds.segment, // 53
  ];

  // Count selected items — group jobs the same way the backend does:
  // one job per ref (or per ref+language for language-dependent refs)
  const jobGroups = new Set<string>();
  let selectedItems = 0;
  selectedCells.forEach((key) => {
    const { ref, courseId } = parseCellKey(key);
    const cell = dashboardData?.data[ref]?.[courseId];
    if (cell && cell.pending > 0) {
      selectedItems += cell.pending;
      if (languageDependentRefs.includes(ref)) {
        const course = dashboardData?.courses.find((c) => c.id === courseId);
        jobGroups.add(`${ref}_${course?.languageId ?? 0}`);
      } else {
        jobGroups.add(`${ref}`);
      }
    }
  });
  const selectedCount = jobGroups.size;

  return (
    <Container>
      <Header>
        <Title>Create Bulk Translation</Title>
      </Header>

      <FormRow>
        <SelectField
          select
          value={mode}
          variant="outlined"
          label="Mode"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMode(parseInt(e.target.value))}
          size="small"
        >
          <MenuItem value={BatchJobMode.MEMORY_ONLY}>Memory Only</MenuItem>
          <MenuItem value={BatchJobMode.FULL}>Memory + AI</MenuItem>
        </SelectField>
        <SelectFieldWide
          select
          value={languageId}
          variant="outlined"
          label="Course Language"
          onChange={handleLanguageChange}
          size="small"
        >
          <MenuItem value={0}>Select language...</MenuItem>
          <MenuItem value={-1}>All Languages</MenuItem>
          {languages.map((l) => (
            <MenuItem key={l.id} value={l.id}>
              {l.name}
            </MenuItem>
          ))}
        </SelectFieldWide>
        <SelectField
          select
          value={dialectId}
          variant="outlined"
          label="Source Dialect"
          onChange={handleDialectChange}
          size="small"
        >
          <MenuItem value={0}>Select dialect...</MenuItem>
          {Object.keys(dialectsByLanguage)
            .sort()
            .map((lang) =>
              dialectsByLanguage[lang].map((d) => (
                <MenuItem key={d.dialectId} value={d.dialectId}>
                  {d.languageName} - {d.dialectName} ({d.dialectCode})
                </MenuItem>
              )),
            )}
        </SelectField>
        <Button variant="contained" size="small" onClick={handleLoad} disabled={!canLoad || model.loadingGrid}>
          {model.loadingGrid ? <CircularProgress size={16} /> : 'Load'}
        </Button>
      </FormRow>

      {model.loadingGrid && !dashboardData && (
        <LoadingContainer>
          <CircularProgress size={32} />
        </LoadingContainer>
      )}

      {dashboardData && (() => {
        const courseRefs = dashboardData.refs.filter((r) => !sharedRefs.includes(r.ref));

        return (
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <Th>Ref Type</Th>
                  {dashboardData.courses.map((course) => (
                    <ThCourse key={course.id}>
                      <CourseHeaderName>{course.name}</CourseHeaderName>
                      <CourseHeaderRange>
                        <RangeInput
                          type="number"
                          value={courseRanges[String(course.id)]?.start ?? course.startLesson}
                          onChange={(e) => handleCourseRangeChange(course.id, 'start', parseInt(e.target.value) || 1)}
                        />
                        <RangeSeparator>-</RangeSeparator>
                        <RangeInput
                          type="number"
                          value={courseRanges[String(course.id)]?.end ?? course.endLesson}
                          onChange={(e) => handleCourseRangeChange(course.id, 'end', parseInt(e.target.value) || 1)}
                        />
                        <ReloadBtn
                          size="small"
                          onClick={() => handleReloadCourse(course.id)}
                          title="Reload this course"
                          disabled={model.isCourseLoading(course.id)}
                        >
                          {model.isCourseLoading(course.id) ? (
                            <CircularProgress size={12} />
                          ) : (
                            <FontAwesomeIcon icon={faSyncAlt} size="xs" />
                          )}
                        </ReloadBtn>
                      </CourseHeaderRange>
                    </ThCourse>
                  ))}
                </tr>
              </thead>
              <tbody>
                {courseRefs.map((ref, idx) => (
                  <Tr key={ref.ref} $odd={idx % 2 === 1}>
                    <TdRefName>{ref.refName}</TdRefName>
                    {dashboardData.courses.map((course) => {
                      const isLoading = model.isCourseLoading(course.id);
                      const cell = dashboardData.data[ref.ref]?.[course.id] || { pending: 0, doing: 0, done: 0 };
                      const total = cell.pending + cell.doing + cell.done;
                      const hasPending = cell.pending > 0;
                      const cellKey = makeCellKey(ref.ref, course.id);
                      const isSelected = selectedCells.has(cellKey);

                      return (
                        <Td key={course.id} $loading={isLoading}>
                          {isLoading ? (
                            <LoadingCell />
                          ) : (
                            total > 0 && (
                              <CellContent>
                                {hasPending && (
                                  <CellCheckbox
                                    size="small"
                                    checked={isSelected}
                                    onChange={() => handleCellToggle(ref.ref, course.id)}
                                  />
                                )}
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: translationProgressRenderer({ value: { ...cell, dialect: dialectId } }),
                                  }}
                                />
                              </CellContent>
                            )
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        );
      })()}

      {/* Footer with selection summary and create button */}
      <Footer>
        {selectedCount > 0 && (
          <Summary>
            {selectedCount} job{selectedCount !== 1 ? 's' : ''} selected — {selectedItems} items
          </Summary>
        )}
        <FooterButtons>
          <Button variant="outlined" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleCreate}
            disabled={selectedCount === 0 || batchModel.creating}
          >
            {batchModel.creating ? <CircularProgress size={16} /> : `Create ${selectedCount} job${selectedCount !== 1 ? 's' : ''}`}
          </Button>
        </FooterButtons>
      </Footer>
    </Container>
  );
};

export default UnifiedBatchCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 10px 16px 0;
`;

const Title = styled.h3`
  margin: 0;
  font-size: 15px;
  font-weight: 600;
`;

const FormRow = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 16px;
  align-items: center;
`;

const SelectField = styled(TextField)`
  min-width: 160px;
`;

const SelectFieldWide = styled(TextField)`
  width: 140px;
  flex-shrink: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 40px;
`;

const TableWrapper = styled.div`
  overflow: auto;
  flex: 1;
  padding: 0 16px;
`;

const Table = styled.table`
  border-collapse: collapse;
`;

const Tr = styled.tr<{ $odd: boolean }>`
  background: ${(p) => (p.$odd ? '#f9f9f9' : 'white')};
`;

const Th = styled.th`
  position: sticky;
  top: 0;
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 6px 10px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: top;
`;

const ThCourse = styled.th`
  position: sticky;
  top: 0;
  background: #e3f2fd;
  border: 1px solid #ddd;
  padding: 4px 6px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
  vertical-align: top;
  width: 150px;
`;

const CourseHeaderName = styled.div`
  margin-bottom: 4px;
`;

const CourseHeaderRange = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const RangeInput = styled.input`
  width: 42px;
  padding: 1px 3px;
  text-align: center;
  border: 1px solid #ccc;
  border-radius: 3px;
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const RangeSeparator = styled.span`
  color: #999;
`;

const ReloadBtn = styled(IconButton)`
  && {
    padding: 2px;
    margin-left: 2px;
  }
`;

const TdRefName = styled.td`
  border: 1px solid #ddd;
  padding: 4px 10px;
  font-weight: 500;
  white-space: nowrap;
  width: 150px;
`;

const Td = styled.td<{ $loading: boolean }>`
  border: 1px solid #ddd;
  padding: 0 4px;
  text-align: center;
  width: 150px;
  opacity: ${(p) => (p.$loading ? 0.4 : 1)};
`;

const CellContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
`;

const CellCheckbox = styled(Checkbox)`
  && {
    padding: 2px;
  }
`;

const LoadingCell = styled.div`
  height: 20px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 3px;
  margin: 2px 4px;
  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  border-top: 1px solid #e0e0e0;
  background: #fafafa;
`;

const Summary = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #333;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
`;
